import { useEffect, useRef, useState } from 'preact/hooks';
import { Fragment } from 'preact';
import {
  config,
  messages,
  visibleMessages,
  inputDraft,
  aiThinking,
  activeLeadCard,
  lastLeadCard,
  pendingLeadCard,
  openPendingLeadCard,
  closeLeadCardWithReopenPill,
  currentLocale,
  digitalChatMode,
  minimizeDigital,
  fullPage,
} from '../../store/widget-store';
import { LeadCaptureCard } from '../LeadCaptureCard';
import { MediaLightbox } from '../web/MediaLightbox';
import { MessageContent, type LightboxTarget } from '../chat/MessageContent';
import { EmojiPicker } from '../chat/EmojiPicker';
import { FaqList } from '../chat/FaqList';
import { OfflineCard } from '../chat/OfflineCard';
import { useImSession } from '../chat/use-im-session';
import { useStickToBottom } from '../chat/use-stick-to-bottom';
import { useBusyHint } from '../chat/use-busy-hint';
import { IconBot, IconPaperclip, IconSend, IconSmile, IconUser } from '../web/icons';
import { LanguageMenu } from '../chat/LanguageMenu';
import { Typewriter } from './Typewriter';
import { QuickQuestionCarousel } from './QuickQuestionCarousel';
import { useI18n } from '../../hooks/use-i18n';
import { pickCsMessage, resolveQuickQuestionText } from '../../i18n/widget-content';
import type { UIMessage } from '../../types';
import type { FrameDragHandleProps } from '../../hooks/use-frame-drag';
import { getAudioPlayer } from '../../services/audio-player';
import { useDragSheet } from './use-drag-sheet';
import { toggleDigitalChatMode, resetDigitalChat, sendDigitalAiText } from './chat-actions';

/**
 * 数字人聊天面板(沉浸式大弹窗右半屏 / 移动端底部拖拽面板)。
 *
 * - AI 模式:走自家 AI 链路(getWsClient),消息由常驻 DigitalRoot 订阅 ai.stream 系列 + ai.audio,
 *   按音频时长逐字回显(displayText);本面板只发消息 + 渲染。
 * - 人工模式:转人工接入 vocechat IM(useImSession),消息规则/表情/附件/文件图标与网页客服一致;
 *   离线展示友好卡片引导加微信,在线接入。每次转人工都会重新检测在线(hook 内 refreshServiceStatus)。
 * - 后台文案:欢迎语 / 引导语 / 快速提问 / 常见问题(FAQ)均按 config 渲染。
 */
export function ChatPane({ dragHandleProps }: { dragHandleProps?: FrameDragHandleProps }) {
  const cfg = config.value;
  const { t } = useI18n();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const { heightPct, handleProps } = useDragSheet();

  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [lightbox, setLightbox] = useState<LightboxTarget | null>(null);

  const mode = digitalChatMode.value;
  const audioEnabled = cfg?.audioEnabled === true;
  const imEnabled = !!cfg?.im?.enabled;
  const im = useImSession(imEnabled && mode === 'human', bodyRef);
  const {
    agentOnline,
    imOffline,
    agentTyping,
    imHasMore,
    imLoadingMore,
    loadMoreIm,
    triggerMyTyping,
    prependingRef,
  } = im;

  // 贴底跟随:用户上滚看历史时暂停吸底(AI 流式期间也可滚动),滚回底部恢复
  const { handleBodyScroll, scrollIfStuck, pinToBottom } = useStickToBottom(bodyRef);
  // 忙碌提示闪现(hook,必须在上方条件 return 之前调用);仅在用户点击禁用态发送
  // 按钮/快捷问题/按回车时触发
  const busyHint = useBusyHint();

  // 自动滚动(翻页 prepend 时跳过一次;仅贴底时跟随)
  useEffect(() => {
    if (prependingRef.current) {
      prependingRef.current = false;
      return;
    }
    scrollIfStuck();
  }, [messages.value, aiThinking.value, agentTyping, mode]);

  if (!cfg) return null;

  const csName = pickCsMessage(cfg, 'name', currentLocale.value);
  const draft = inputDraft.value;
  // 按当前模式隔离会话:人工模式只显人工 IM 记录,AI 模式只显 AI 对话
  const visible = visibleMessages(messages.value, mode);
  // 留资卡锚点:卡在触发它的 AI 回答下方内联渲染;锚点缺失(旧数据/锚消息不在可见列表)
  // 时回落到消息流末尾(底部兜底渲染)。
  const leadCardAnchorId = activeLeadCard.value?.anchorMsgId ?? null;
  const leadCardAnchored =
    !!leadCardAnchorId && visible.some((m) => m.id === leadCardAnchorId);
  // 留资再入口 pill 锚点:与卡片同款逻辑,pill 钉在触发回答正下方而非沉到消息流末尾
  const leadPillMsg = visible.find((m) => m.meta?.kind === 'lead-reopen') ?? null;
  const leadPillAnchorId = leadPillMsg?.meta?.anchorMsgId ?? null;
  const leadPillAnchored =
    !!leadPillMsg && !!leadPillAnchorId && visible.some((m) => m.id === leadPillAnchorId);
  // 暂存期 pill(弹卡闸门持有,口播/回复中可点提前展开):锚定正在输出的气泡下方
  const pendingPillAnchorId = pendingLeadCard.value?.anchorMsgId || null;
  const pendingPillAnchored =
    !!pendingPillAnchorId && visible.some((m) => m.id === pendingPillAnchorId);

  /** 留资再入口 pill(dismiss/收起后,消息列表里的 system 消息形态;居中小药丸) */
  const renderLeadReopenPill = (m: UIMessage) => (
    <div key={m.id} class="dcs-sys-center">
      <span class="dcs-sr-only">{t(`roles.${m.role}`)}</span>
      <button
        type="button"
        class="dcs-sys-pill dcs-lead-reopen-pill"
        onClick={() => {
          if (lastLeadCard.value) activeLeadCard.value = lastLeadCard.value;
        }}
        aria-label={m.text}
      >
        <span class="dcs-lead-reopen-icon" aria-hidden="true">▶</span>
        {m.text}
      </button>
    </div>
  );

  /** 暂存期 pill(派生 UI,不进消息列表):点击立即展开并清暂存 */
  const renderPendingLeadPill = () => (
    <div class="dcs-sys-center">
      <button
        type="button"
        class="dcs-sys-pill dcs-lead-reopen-pill"
        onClick={openPendingLeadCard}
        aria-label={t('panel.leadReopenHint')}
      >
        <span class="dcs-lead-reopen-icon" aria-hidden="true">▶</span>
        {t('panel.leadReopenHint')}
      </button>
    </div>
  );
  // AI 回合进行中(思考/流式/口播)禁发,防并发回合异常(BE 另有 per-convId 串行兜底);
  // 仅 AI 模式闸门,人工 IM 消息不触发回合,不拦。
  const aiBusy =
    mode === 'ai' &&
    (aiThinking.value || visible.some((m) => m.role === 'ai' && m.streaming === true));

  const sendText = (text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    // 防御:UI 按钮/回车已拦,这里兜底快捷问题等旁路;命中也给点击者一个反馈
    if (aiBusy) {
      busyHint.flash();
      return;
    }
    pinToBottom(); // 自己发消息必须立即可见,恢复贴底跟随
    // 新回合自动收起还开着的留资卡(AI 分支 sendDigitalAiText 内也有同样调用,幂等)
    closeLeadCardWithReopenPill();
    if (audioEnabled) getAudioPlayer().unlock(); // 用户手势内解锁 autoplay(仅启用语音时)
    if (mode === 'human' && imEnabled) {
      im.sendText(trimmed);
    } else {
      // AI 链路核心(乐观气泡 + 思考态 + SSE 发出)与欢迎屏引导问题共用,见 chat-actions
      sendDigitalAiText(trimmed);
    }
    inputDraft.value = '';
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'; // 自增高 textarea:发完缩回单行
      inputRef.current.focus();
    }
  };

  const sendDraft = () => sendText(draft);
  const handleQuickQuestion = (text: string) => sendText(text);

  const handleEmojiSelect = (emoji: string) => {
    inputDraft.value = (inputDraft.value || '') + emoji;
    setShowEmojiPicker(false);
    inputRef.current?.focus();
  };

  const handleFileUpload = (e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;
    if (mode === 'human' && imEnabled) im.sendFile(file);
    target.value = '';
  };

  // 转人工 / 返回AI、重置 两个会话动作已提取到 ./chat-actions,
  // 桌面端 header 与移动端 ImmersiveShell 顶部悬浮组共用同一份逻辑。

  const showOfflineCard = mode === 'human' && !agentOnline;
  const showInput = !showOfflineCard;
  // 快捷问题栏常驻(AI 模式):不再是空会话一次性 onboarding;
  // AI 回合进行中按钮禁用 + hover 提示(aiBusyHint),回合结束自动解锁。
  // 非中文 locale 无译文条目会被 Carousel 过滤;全无时整个快速提问区(含标题)不渲染
  const showQuickQuestions =
    mode === 'ai' &&
    cfg.quickQuestions.some(
      (q) => resolveQuickQuestionText(q, currentLocale.value) !== null,
    );

  return (
    <section
      class="dcs-chat-pane"
      role="region"
      aria-label={csName}
      style={`--dcs-sheet-h:${heightPct}%`}
    >
      <div class="dcs-sheet-handle" {...handleProps} aria-hidden="true" />

      {/* header 兼作桌面端弹窗拖动把手:空白区可拖,内部按钮/语言菜单经 hitInteractive 自动排除 */}
      <header class="dcs-panel-header dcs-chat-header" {...(dragHandleProps ?? {})}>
        {cfg.avatarUrl ? (
          <img src={cfg.avatarUrl} alt="" />
        ) : (
          <div class="dcs-panel-header-avatar-placeholder" />
        )}
        <div class="dcs-panel-header-text">
          <span class="dcs-panel-name">{csName}</span>
          <span class="dcs-panel-status">
            {mode === 'ai' || agentOnline
              ? t('panel.onlineStatus')
              : t('web.offlineLabel')}
          </span>
        </div>

        <LanguageMenu />

        {/* 转人工 / 返回 AI */}
        <button
          type="button"
          class="dcs-icon-btn"
          aria-label={mode === 'ai' ? t('web.switchToHuman') : t('web.switchToAI')}
          title={mode === 'ai' ? t('web.switchToHuman') : t('web.switchToAI')}
          onClick={() => toggleDigitalChatMode()}
        >
          {mode === 'ai' ? <IconUser size={20} /> : <IconBot size={20} />}
        </button>

        {/* 重置仅 AI 会话可用 */}
        {mode === 'ai' && messages.value.length > 0 && (
          <button
            type="button"
            class="dcs-icon-btn"
            aria-label={t('web.resetChat')}
            title={t('web.resetChat')}
            onClick={() => void resetDigitalChat(t)}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
            </svg>
          </button>
        )}

        {/* 整页 H5 模式隐藏关闭键(整页即客服,退出交给小程序原生导航) */}
        {!fullPage.value && (
          <button
            type="button"
            class="dcs-icon-btn"
            aria-label={t('panel.closeAria')}
            onClick={minimizeDigital}
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </header>

      <div
        class="dcs-panel-body"
        role="log"
        aria-live="polite"
        aria-label={csName}
        ref={bodyRef}
        onScroll={(e) => {
          const el = e.currentTarget as HTMLDivElement;
          handleBodyScroll(el);
          if (mode !== 'human' || !imEnabled) return;
          if (el.scrollTop <= 24 && imHasMore && !imLoadingMore) void loadMoreIm();
        }}
      >
        {showOfflineCard ? (
          <OfflineCard cfg={cfg} imOffline={imOffline} onBackToAi={() => toggleDigitalChatMode()} t={t} />
        ) : (
          <>
            {mode === 'ai' && (
              <div class="dcs-msg dcs-msg-ai dcs-welcome-row">
                <span class="dcs-msg-avatar is-ai" aria-hidden="true">
                  {cfg.avatarUrl ? <img src={cfg.avatarUrl} alt="" /> : <IconBot size={18} />}
                </span>
                <div class="dcs-welcome">
                  <Typewriter
                    text={
                      pickCsMessage(cfg, 'welcomeMsg', currentLocale.value) ||
                      t('panel.welcomeFallback')
                    }
                    // 已有历史记录(回看旧会话)时直接全显欢迎语,不再逐字"打招呼";
                    // 仅全新空会话首次进入才播打字机。重置会清空 messages → 自动恢复打字。
                    enabled={visible.length === 0}
                  />
                </div>
              </div>
            )}

            {/* 人工招呼行 —— 转人工接通后(主体仅在坐席在线时渲染,招呼天然只在接通后出现)主动打招呼
                引导访客,对标 AI 欢迎行;头像兜底用人工图标,文案取 humanWelcomeMsg,空则回退内置招呼语。 */}
            {mode === 'human' && (
              <div class="dcs-msg dcs-msg-ai dcs-welcome-row">
                <span class="dcs-msg-avatar is-ai" aria-hidden="true">
                  {cfg.avatarUrl ? <img src={cfg.avatarUrl} alt="" /> : <IconUser size={18} />}
                </span>
                <div class="dcs-welcome">
                  <Typewriter
                    text={
                      pickCsMessage(cfg, 'humanWelcomeMsg', currentLocale.value) ||
                      t('web.humanGreeting')
                    }
                    // 与 AI 欢迎行一致:有人工 IM 历史时直接全显,空会话首次进入才逐字播。
                    enabled={visible.length === 0}
                  />
                </div>
              </div>
            )}

            {/* 常见问题 FAQ — 仅聊天页空消息态展示(与网页客服一致,accordion 就地展开) */}
            {mode === 'ai' && visible.length === 0 && cfg.faqs.length > 0 && (
              <FaqList faqs={cfg.faqs} locale={currentLocale.value} t={t} />
            )}

            {visible.map((m) => {
              if (m.role === 'system' && m.meta?.kind === 'lead-reopen') {
                if (lastLeadCard.value === null) return null;
                // 锚定命中:跳过原始位置,挪到锚消息正下方渲染(见下方消息循环);
                // 未命中回落原位(居中小药丸,对齐网页客服 .dcs-web-system)。
                if (leadPillAnchored && m === leadPillMsg) return null;
                return renderLeadReopenPill(m);
              }
              const isAi = m.role === 'ai';
              // 启用语音:显 displayText(打字机);未启用:实时流式显 text(web 式)。
              // displayText 缺省时回退 m.text(契约见 UIMessage.displayText 注释):
              // 历史 AI 消息无 displayText 时直接显完整 text,避免空气泡;实时流式阶段
              // text 全程为空(文本累积在 fullText),回退后仍为空 → 照常显三点动画。
              const shown = isAi ? (audioEnabled ? (m.displayText ?? m.text) : m.text) : m.text;
              // 仅启用语音时:delta 累积阶段(还没开始逐字)显三点动画,不显空气泡
              const showDots = isAi && m.streaming && audioEnabled && !shown;
              const isVisitor = m.role === 'visitor';
              return (
                <Fragment key={m.id}>
                  <div class={`dcs-msg dcs-msg-${m.role}`}>
                    <span class="dcs-sr-only">{t(`roles.${m.role}`)}</span>
                    {!isVisitor && (
                      <span class="dcs-msg-avatar is-ai" aria-hidden="true">
                        {cfg.avatarUrl ? <img src={cfg.avatarUrl} alt="" /> : <IconBot size={18} />}
                      </span>
                    )}
                    {showDots ? (
                      <div class="dcs-msg-text dcs-thinking-dots" aria-hidden="true">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    ) : (
                      <div class="dcs-msg-text">
                        <MessageContent
                          msg={m}
                          t={t}
                          onOpenLightbox={setLightbox}
                          text={isAi ? shown : undefined}
                        />
                      </div>
                    )}
                    {isVisitor && (
                      <span class="dcs-msg-avatar is-user" aria-hidden="true">
                        <IconUser size={18} />
                      </span>
                    )}
                  </div>
                  {/* 留资卡锚定渲染:贴在触发它的这条 AI 回答正下方 */}
                  {leadCardAnchorId === m.id && <LeadCaptureCard />}
                  {/* 留资 pill 锚定渲染:钉在触发回答正下方(原位已跳过) */}
                  {leadPillAnchored && leadPillAnchorId === m.id && renderLeadReopenPill(leadPillMsg!)}
                  {/* 暂存期 pill:口播/回复进行中可点提前展开 */}
                  {pendingPillAnchored && pendingPillAnchorId === m.id && renderPendingLeadPill()}
                </Fragment>
              );
            })}

            {mode === 'ai' && aiThinking.value && (
              <div
                class="dcs-msg dcs-msg-ai dcs-thinking"
                role="status"
                aria-label={t('panel.thinkingAria')}
              >
                <span class="dcs-msg-avatar is-ai" aria-hidden="true">
                  {cfg.avatarUrl ? <img src={cfg.avatarUrl} alt="" /> : <IconBot size={18} />}
                </span>
                <div class="dcs-msg-text dcs-thinking-dots" aria-hidden="true">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            {/* 人工坐席「正在输入」提示 */}
            {mode === 'human' && agentTyping && (
              <div class="dcs-msg dcs-msg-human" role="status">
                <span class="dcs-msg-avatar is-ai" aria-hidden="true">
                  {cfg.avatarUrl ? <img src={cfg.avatarUrl} alt="" /> : <IconBot size={18} />}
                </span>
                <div class="dcs-msg-text dcs-thinking-dots" aria-hidden="true">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}

            {/* 留资卡兜底渲染:锚点缺失(旧数据/锚消息不可见)时落在消息流末尾;
                锚定生效时已在上方 map 里内联渲染,这里不再重复 */}
            {!leadCardAnchored && <LeadCaptureCard />}
            {/* 暂存期 pill 兜底:锚点缺失(空锚待重绑/锚消息不可见)时落在消息流末尾 */}
            {pendingLeadCard.value && !pendingPillAnchored && renderPendingLeadPill()}
          </>
        )}
      </div>

      {showInput && (
        <footer class="dcs-chat-footer">
          {showQuickQuestions && (
            <div class="dcs-chat-quick">
              <span class="dcs-chat-quick-title">{t('panel.quickAskTitle')}</span>
              <QuickQuestionCarousel
                questions={cfg.quickQuestions}
                onPick={handleQuickQuestion}
                disabled={aiBusy}
                disabledHint={t('panel.aiBusyHint')}
              />
            </div>
          )}

          {showEmojiPicker && mode === 'human' && (
            <EmojiPicker onSelect={handleEmojiSelect} />
          )}

          {/* 输入框对齐网页客服:圆角灰底 wrap 内含自增高 textarea + 末尾动作组
              (表情 / 附件 仅人工模式,发送图标常驻),复用 dcs-web-input-* 样式。 */}
          <div class="dcs-web-input-wrap">
            <textarea
              ref={inputRef}
              class="dcs-web-input"
              placeholder={t('panel.inputPlaceholder')}
              rows={1}
              value={draft}
              onInput={(e) => {
                const ta = e.currentTarget as HTMLTextAreaElement;
                inputDraft.value = ta.value;
                ta.style.height = 'auto';
                ta.style.height = Math.min(ta.scrollHeight, 128) + 'px';
                triggerMyTyping();
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  if (aiBusy) {
                    busyHint.flash(); // 回合进行中:给提示,不发送
                    return;
                  }
                  sendDraft();
                }
              }}
              aria-label={t('panel.inputPlaceholder')}
            />
            <div class="dcs-web-input-actions">
              {/* 忙碌提示小气泡:点击禁用态发送按钮/快捷问题/按回车时在按钮上方弹出 */}
              {aiBusy && busyHint.visible && (
                <div class="dcs-send-busy-tip" role="status">
                  {t('panel.aiBusyHint')}
                </div>
              )}
              {mode === 'human' && (
                <>
                  <button
                    type="button"
                    class="dcs-web-input-icon"
                    onClick={() => setShowEmojiPicker((v) => !v)}
                    aria-label="emoji"
                  >
                    <IconSmile size={20} />
                  </button>
                  <button
                    type="button"
                    class="dcs-web-input-icon"
                    onClick={() => fileInputRef.current?.click()}
                    aria-label="attach"
                  >
                    <IconPaperclip size={20} />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    class="dcs-web-file-input"
                    accept="image/*,video/*,.pdf,.doc,.docx"
                    onChange={handleFileUpload}
                  />
                </>
              )}
              <button
                type="button"
                class={`dcs-send-btn dcs-web-send-btn${aiBusy ? ' is-busy' : ''}`}
                aria-label={t('panel.sendAria')}
                disabled={!draft.trim()}
                aria-disabled={aiBusy || undefined}
                title={aiBusy ? t('panel.aiBusyHint') : t('panel.sendAria')}
                onClick={() => {
                  // 忙碌时按钮保留可点(原生 disabled 不触发 click,给不了反馈):点击闪现提示
                  if (aiBusy) {
                    busyHint.flash();
                    return;
                  }
                  sendDraft();
                }}
                style={
                  cfg.theme
                    ? { background: cfg.theme, '--dcsw-send-theme': cfg.theme }
                    : undefined
                }
              >
                <IconSend size={16} />
              </button>
            </div>
          </div>
        </footer>
      )}

      {/* 图片/视频放大查看 —— 放在沉浸层之外,避免被 transform 包含块裁切 */}
      {lightbox && (
        <MediaLightbox
          key={lightbox.url}
          type={lightbox.type}
          url={lightbox.url}
          name={lightbox.name}
          onClose={() => setLightbox(null)}
        />
      )}
    </section>
  );
}
